import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { sankey, sankeyLinkHorizontal } from 'd3-sankey';


const SankeyChart = ({ data, formatCurrency, height = 500, categories = [], focusedCategory }) => {
  const svgRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  
  // ResizeObserver to update width
  useEffect(() => {
    const handleResize = () => {
      if (svgRef.current) {
        setContainerWidth(svgRef.current.clientWidth);
      }
    };
    handleResize(); // set initial width

    const resizeObserver = new window.ResizeObserver(handleResize);
    if (svgRef.current) {
      resizeObserver.observe(svgRef.current);
    }
    return () => {
      if (svgRef.current) {
        resizeObserver.unobserve(svgRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!svgRef.current || !data || !containerWidth) return;
    
    // Build a color map from categories (by name, fallback to id if needed)
    const colorMap = {};
    categories.forEach(cat => {
      if (cat.name) colorMap[cat.name] = cat.color;
      if (cat.id) colorMap[cat.id] = cat.color;
    });
    // Fallback colors
    const fallbackColors = {
      income: '#10B981',
      expenses: '#EF4444',
      savings: '#3B82F6',
      general: '#6366F1',
      other: '#A3A3A3',
      netBalance: '#3B82F6',
    };
    
    // Clear any existing SVG content
    d3.select(svgRef.current).selectAll("*").remove();
    
    // Setup dimensions
    const margin = { top: 10, right: 10, bottom: 10, left: 10 };
    const width = containerWidth - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;
    
    // Create SVG
    const svg = d3.select(svgRef.current)
      .attr("width", width + margin.left + margin.right)
      .attr("height", chartHeight + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);
    
    // Create Sankey generator
    const sankeyGenerator = sankey()
      .nodeWidth(30)                // wider boxes
      .nodePadding(20)              // more vertical spacing
      .extent([[0, 0], [width, chartHeight]]);
    
    // Generate layout
    const sankeyData = sankeyGenerator({
      nodes: data.nodes.map(d => ({ ...d })),
      links: data.links.map(d => ({ ...d }))
    });
    
    // capture max link width for relative opacity
    const maxLink = d3.max(sankeyData.links, d => d.width);
    
    // Draw the links (flow paths)
    svg.append("g")
      .selectAll(".link")
      .data(sankeyData.links)
      .enter()
      .append("path")
      .attr("class", "link")
      .attr("d", sankeyLinkHorizontal())
      .attr("stroke", d => {
        // Use category color if available, fallback otherwise
        const sourceColor = colorMap[d.source.name] || fallbackColors[d.source.category] || fallbackColors.other;
        const targetColor = colorMap[d.target.name] || fallbackColors[d.target.category] || fallbackColors.other;
        // Prefer source color for income, target color for savings, else expense color
        if (d.source.category === 'income') return sourceColor;
        if (d.target.category === 'savings') return targetColor;
        if (d.target.name === 'Other') return fallbackColors.other;
        return targetColor;
      })
      .attr("stroke-width", d => {
        const isFocused = focusedCategory && (d.source.name === focusedCategory || d.target.name === focusedCategory);
        return isFocused ? Math.max(4, d.width * 1.2) : Math.max(2, d.width);
      })
      .attr("opacity", d => {
        if (!focusedCategory) return 0.2 + (d.width / maxLink) * 0.6;
        const isFocused = d.source.name === focusedCategory || d.target.name === focusedCategory;
        return isFocused ? 0.9 : 0.15;
      })
      .attr("fill", "none")
      .on("mouseover", function() {
        d3.select(this)
          .attr("opacity", 0.9)
          .attr("stroke-width", d => Math.max(2, d.width * 1.2));
      })
      .on("mouseout", function() {
        d3.select(this)
          .attr("opacity", d => {
            if (!focusedCategory) return 0.2 + (d.width / maxLink) * 0.6;
            const isFocused = d.source.name === focusedCategory || d.target.name === focusedCategory;
            return isFocused ? 0.9 : 0.15;
          })
          .attr("stroke-width", d => {
            const isFocused = focusedCategory && (d.source.name === focusedCategory || d.target.name === focusedCategory);
            return isFocused ? Math.max(4, d.width * 1.2) : Math.max(2, d.width);
          });
      })
      .append("title")
      .text(d => `${d.source.name} → ${d.target.name}\n${formatCurrency(d.value)}`);
    
    // Draw the nodes (boxes)
    const node = svg.append("g")
      .selectAll(".node")
      .data(sankeyData.nodes)
      .enter()
      .append("g")
      .attr("class", "node")
      .attr("transform", d => `translate(${d.x0},${d.y0})`);
    
    // Add rectangles for nodes
    node.append("rect")
      .attr("height", d => d.y1 - d.y0)
      .attr("width", d => d.x1 - d.x0)
      // Subtle rounded corners that work well with flow paths
      .attr("rx", 4)
      .attr("ry", 4)
      .attr("fill", d => {
        if (d.name === 'Other') return fallbackColors.other;
        if (d.name === 'Net Balance') return fallbackColors.netBalance;
        if (d.name === 'Total Income') return fallbackColors.general;
        return colorMap[d.name] || fallbackColors[d.category] || fallbackColors.other;
      })
      .attr("stroke", "#fff")          // clean white outline
      .attr("stroke-width", 2)
      .attr("opacity", d => {
        if (!focusedCategory) return 1;
        return d.name === focusedCategory ? 1 : 0.3;
      });
    
  // Calculate totals for percentages
  const totalIncome = sankeyData.nodes.filter(d => d.category === 'income').reduce((sum, d) => sum + d.value, 0);
  const totalExpenses = sankeyData.nodes.filter(d => d.category === 'expenses').reduce((sum, d) => sum + d.value, 0);

  // Responsive label sizing — slightly larger than previous across sizes
  const sizeScale = width < 480 ? 0.95 : width < 768 ? 0.95 : width < 1024 ? 0.95 : 1.0;
  const fontSize = Math.max(8, Math.round(10 * sizeScale));
  const labelPaddingX = Math.max(6, Math.round(8 * sizeScale)); // horizontal padding
  const labelPaddingY = Math.max(3, Math.round(4 * sizeScale)); // vertical padding
  const chipCornerRadius = 6; // pill corner radius
    const labelsGroup = svg.append("g").attr("class", "labels");
    labelsGroup.selectAll("g")
      .data(sankeyData.nodes)
      .enter()
      .append("g")
      .each(function(d) {
        let percent = null;
        if (d.category === 'income' && totalIncome > 0) {
          percent = (d.value / totalIncome) * 100;
        } else if (d.category === 'expenses' && totalExpenses > 0) {
          percent = (d.value / totalExpenses) * 100;
        } else if (d.name === 'Net Balance' && totalIncome > 0) {
          percent = (d.value / totalIncome) * 100;
        }
        const nameText = d.name === 'Total Income' ? 'Total' : d.name;
  const pctText = percent !== null ? `${Math.round(percent)}%` : null;
        const valueText = pctText ? `: ${formatCurrency(d.value)} (${pctText})` : `: ${formatCurrency(d.value)}`;
        const x = d.x0 + (d.category === 'income' ? (d.x1 - d.x0) + 6 : (d.x1 - d.x0) - 6);
        const y = d.y0 + (d.y1 - d.y0) / 2;
        const anchor = d.category === 'income' ? 'start' : 'end';
        // Render text first (hidden) to measure
        const tempText = d3.select(this)
          .append('text')
          .attr('x', x)
          .attr('y', y)
          .attr('dy', '0.35em')
          .attr('text-anchor', anchor)
          .attr('font-size', fontSize + 'px')
          .attr('opacity', 0);
        // Only category name bold, amount/percent normal
        tempText.append('tspan')
          .attr('font-weight', 700)
          .text(nameText);
        tempText.append('tspan')
          .attr('font-weight', 400)
          .text(valueText);
        // Use getBBox to measure
        let bbox = tempText.node().getBBox();
        // Clamp text so it doesn't extend past the left edge of the chart
        if (bbox.x < -margin.left) {
          const shift = -margin.left - bbox.x + labelPaddingX;
          tempText.attr('x', parseFloat(tempText.attr('x')) + shift)
                  .attr('text-anchor', 'start');
          bbox = tempText.node().getBBox();
        }
        // Calculate pill position and size
        const pillWidth = bbox.width + labelPaddingX * 2;
        const pillHeight = bbox.height + labelPaddingY * 2;
        const pillX = bbox.x - labelPaddingX;
        const pillY = bbox.y - labelPaddingY;
        // Only show pill for Total Income if not on small screen
        const pillClass = d.name === 'Total Income' ? 'sankey-total-income-label' : null;
        // Draw pill
        d3.select(this)
          .insert('rect', 'text')
          .attr('x', pillX)
          .attr('y', pillY)
          .attr('width', pillWidth)
          .attr('height', pillHeight)
          .attr('rx', chipCornerRadius)
          .attr('ry', chipCornerRadius)
          .attr('fill', 'white')
          .attr('stroke', '#ddd')
          .attr('stroke-width', 1)
          .attr('opacity', 0.45) // more transparent background
          .attr('stroke-opacity', 0.5)
          .attr('class', pillClass);
        // Show text (now visible)
  tempText
          .attr('opacity', !focusedCategory ? 0.65 : (d.name === focusedCategory ? 0.65 : 0.2)) // more transparent text
          .attr('fill', '#000')
          .attr('class', d.name === 'Total Income' ? 'sankey-total-income-label' : null);
      });
    
  }, [data, formatCurrency, height, categories, containerWidth, focusedCategory]);
  
  return <svg ref={svgRef} style={{ width: '100%', height, overflow: 'visible' }}></svg>;
};

export default SankeyChart;